import React from 'react';
import { Input, DatePicker, Select, Card, Row, Col, Typography } from 'antd';
import dayjs from 'dayjs';
import { FN110 } from '../../../services/n202Api';
import { recordTypeLabel, fmt } from '../helpers';

const { TextArea } = Input;
const { Text } = Typography;

interface Tab1Props {
  formData: Partial<FN110>;
  isEditing: boolean;
  isFieldDisabled: (key: string) => boolean;
  getField: (key: keyof FN110) => FN110[keyof FN110];
  setField: (key: keyof FN110, value: FN110[keyof FN110]) => void;
}

/** Vendor summary sub-card */
const VendorSummary: React.FC<{
  label: string;
  vendorCode: string;
  currency: string;
  rate: number;
  deduction: number;
  returnRefund: number;
  refund: number;
  buyer: string;
  buyerDept: string;
  detailCount: number;
}> = ({ label, vendorCode, currency, rate, deduction, returnRefund, refund, buyer, buyerDept, detailCount }) => (
  <Card size="small" title={label} style={{ marginBottom: 8 }}>
    <Row gutter={[8, 4]}>
      <Col span={8}><Text type="secondary">廠商代號</Text></Col>
      <Col span={16}><Text strong>{vendorCode || '--'}</Text></Col>
      <Col span={8}><Text type="secondary">幣別/匯率</Text></Col>
      <Col span={16}><Text>{currency || '--'} / {fmt(rate)}</Text></Col>
      <Col span={8}><Text type="secondary">貨款抵扣</Text></Col>
      <Col span={16}><Text>{fmt(deduction)}</Text></Col>
      <Col span={8}><Text type="secondary">進貨退款</Text></Col>
      <Col span={16}><Text>{fmt(returnRefund)}</Text></Col>
      <Col span={8}><Text type="secondary">退款</Text></Col>
      <Col span={16}><Text>{fmt(refund)}</Text></Col>
      <Col span={8}><Text type="secondary">採購人員</Text></Col>
      <Col span={16}><Text>{buyer || '--'} ({buyerDept || '--'})</Text></Col>
      <Col span={8}><Text type="secondary">明細筆數</Text></Col>
      <Col span={16}><Text>{detailCount}</Text></Col>
    </Row>
  </Card>
);

const Tab1BasicInfo: React.FC<Tab1Props> = ({
  formData, isEditing, isFieldDisabled, getField, setField,
}) => {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      {/* Left: Master fields */}
      <div>
        <Card size="small" title="基本資料" style={{ marginBottom: 8 }}>
          <Row gutter={[12, 8]}>
            <Col span={6}><Text type="secondary">客訴單號</Text></Col>
            <Col span={18}>
              <Input
                size="small"
                value={getField('fn110_01') as string}
                disabled
                style={{ maxWidth: 200 }}
              />
            </Col>

            <Col span={6}><Text type="secondary">客訴日期</Text></Col>
            <Col span={18}>
              <DatePicker
                size="small"
                disabled={isFieldDisabled('fn110_11')}
                value={formData.fn110_11 ? dayjs(formData.fn110_11) : null}
                onChange={d => setField('fn110_11', d ? d.toISOString() : null as unknown as string)}
                style={{ width: 160 }}
              />
            </Col>

            <Col span={6}><Text type="secondary">部門代碼</Text></Col>
            <Col span={18}>
              <Input
                size="small"
                disabled={isFieldDisabled('fn110_22')}
                value={getField('fn110_22') as string}
                onChange={e => setField('fn110_22', e.target.value)}
                style={{ maxWidth: 160 }}
              />
            </Col>

            <Col span={6}><Text type="secondary">記錄型態</Text></Col>
            <Col span={18}>
              <Select
                size="small"
                disabled={isFieldDisabled('fn110_23')}
                value={formData.fn110_23}
                onChange={v => setField('fn110_23', v)}
                style={{ width: 140 }}
                options={[
                  { value: 1, label: recordTypeLabel(1) },
                  { value: 2, label: recordTypeLabel(2) },
                  { value: 3, label: recordTypeLabel(3) },
                ]}
              />
            </Col>

            <Col span={6}><Text type="secondary">建立者</Text></Col>
            <Col span={18}>
              <Input
                size="small"
                value={getField('fn110_132') as string}
                disabled
                style={{ maxWidth: 160 }}
              />
            </Col>

            <Col span={6}><Text type="secondary">關聯業務單號</Text></Col>
            <Col span={18}>
              <Input
                size="small"
                disabled={isFieldDisabled('fn110_71')}
                value={getField('fn110_71') as string}
                onChange={e => setField('fn110_71', e.target.value)}
                style={{ maxWidth: 200 }}
              />
            </Col>
          </Row>
        </Card>

        <Card size="small" title="採購處理說明" style={{ marginBottom: 8 }}>
          <TextArea
            rows={4}
            disabled={isFieldDisabled('fn110_28')}
            value={getField('fn110_28') as string}
            onChange={e => setField('fn110_28', e.target.value)}
          />
        </Card>

        {/* Approval status summary */}
        <Card size="small" title="核准狀態">
          <Row gutter={[12, 4]}>
            <Col span={8}><Text type="secondary">業務核准</Text></Col>
            <Col span={16}><Text>{formData.fn110_80 ? 'Y' : 'N'}</Text></Col>
            <Col span={8}><Text type="secondary">採購核准</Text></Col>
            <Col span={16}><Text>{formData.fn110_81 ? 'Y' : 'N'}</Text></Col>
            <Col span={8}><Text type="secondary">副總核准</Text></Col>
            <Col span={16}><Text>{formData.fn110_82 ? 'Y' : 'N'}</Text></Col>
            <Col span={8}><Text type="secondary">結案</Text></Col>
            <Col span={16}><Text>{formData.fn110_84 ? 'Y' : 'N'}</Text></Col>
            <Col span={8}><Text type="secondary">作廢</Text></Col>
            <Col span={16}><Text>{formData.fn110_801 ? 'Y' : 'N'}</Text></Col>
            <Col span={8}><Text type="secondary">版本</Text></Col>
            <Col span={16}><Text>{formData.fn110_90 || 0}</Text></Col>
          </Row>
        </Card>
      </div>

      {/* Right: Vendor summaries */}
      <div>
        <VendorSummary
          label="廠商一"
          vendorCode={formData.fn110_41 || ''}
          currency={formData.fn110_42 || ''}
          rate={formData.fn110_43 || 0}
          deduction={formData.fn110_44 || 0}
          returnRefund={formData.fn110_45 || 0}
          refund={formData.fn110_46 || 0}
          buyer={formData.fn110_49 || ''}
          buyerDept={formData.fn110_221 || ''}
          detailCount={formData.fn110_48 || 0}
        />
        <VendorSummary
          label="廠商二"
          vendorCode={formData.fn110_51 || ''}
          currency={formData.fn110_52 || ''}
          rate={formData.fn110_53 || 0}
          deduction={formData.fn110_54 || 0}
          returnRefund={formData.fn110_55 || 0}
          refund={formData.fn110_56 || 0}
          buyer={formData.fn110_59 || ''}
          buyerDept={formData.fn110_222 || ''}
          detailCount={formData.fn110_58 || 0}
        />
        <VendorSummary
          label="廠商三"
          vendorCode={formData.fn110_61 || ''}
          currency={formData.fn110_62 || ''}
          rate={formData.fn110_63 || 0}
          deduction={formData.fn110_64 || 0}
          returnRefund={formData.fn110_65 || 0}
          refund={formData.fn110_66 || 0}
          buyer={formData.fn110_69 || ''}
          buyerDept={formData.fn110_223 || ''}
          detailCount={formData.fn110_68 || 0}
        />
      </div>
    </div>
  );
};

export default React.memo(Tab1BasicInfo);
